import { Back } from "gsap/EasePack";
import { TweenLite } from "gsap/TweenLite";
import { PixelPerfectHitArea } from "html-living-framework/lib/Input";
import { makeSprite } from "html-living-framework/lib/Utils/PixiUtils";

import { Application } from "Application";
import { SPRITESHEET_UI } from "Data/Constants";
import { GameConfig, ItemID } from "Data/GameConfig";
import { ALL_ITEMS_FOUND, ITEM_FOUND } from "Data/Topics";
import { ExteriorItemIcon } from "GameObjects/GameExterior/ExteriorItemIcon";
import type { ILevelState } from "Interfaces/ILevelState";

const MAX_ICON_SIZE = 52;
const GAP_BETWEEN = 7;
const X_OFFSET = 80;
const PADDING_BOTTOM = 4;
const PADDING_RIGHT = 44;
const MAX_ITEMS = 8;

export class ProgressTracker extends PIXI.Container {

    protected _subscriptions: any[] = [];
    protected allItemsFound: boolean = false;
    protected icons: ExteriorItemIcon[] = [];

    constructor() {
        super();
        this._subscriptions.push(
            PubSub.subscribe(ITEM_FOUND, this.onMessageReceived.bind(this)),
            PubSub.subscribe(ALL_ITEMS_FOUND, this.onMessageReceived.bind(this)),
        );
    }

    public init(pData: ILevelState) {

        const bg = this.addChild(makeSprite("items-container-long", SPRITESHEET_UI));
        bg.anchor.set(0, 1);
        bg.position.set(X_OFFSET, 0);
        bg.interactive = true;
        bg.hitArea = new PixelPerfectHitArea(Application.instance.renderer, bg);

        const container = this.addChild(new PIXI.Container());

        let newSprite: ExteriorItemIcon;
        const itemIDs = Object.keys(pData.items) as ItemID[];
        const spacing = MAX_ICON_SIZE;

        let x = bg.width + X_OFFSET - PADDING_RIGHT;
        let i = 0;

        for (const itemID of itemIDs) {
            const config = GameConfig.locations[pData.id].items[itemID];
            const state = pData.items[itemID];
            if (state === undefined || config === undefined) {
                throw new Error(`Unexpected item id "${itemID}"`);
            }

            newSprite = container.addChild(new ExteriorItemIcon(config, state, pData));
            this.icons.push(newSprite);
            newSprite.position.x = x;

            x -= (spacing + GAP_BETWEEN) / 2;
            if (i < itemIDs.length - 1) {
                const divider = container.addChildAt(makeSprite("hud-div", SPRITESHEET_UI), 0);
                divider.x = x;
                divider.y = -(PADDING_BOTTOM / 2);
            }
            x -= (spacing + GAP_BETWEEN) / 2;

            i++;
        }

        this.position.x -= (MAX_ITEMS - itemIDs.length) * MAX_ICON_SIZE;
        container.position.y = -((container.height - PADDING_BOTTOM) / 2);
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        TweenLite.killTweensOf(this);
        this._subscriptions.forEach(PubSub.unsubscribe);
        super.destroy(pOptions);
    }

    protected PlayItemFoundAnimation() {
        // new TimelineLite()
        //     .to(this, 0.25, {pixi: {scale: 1.3}})
        //     .to(this, 0.25, {pixi: {scale: 1}});
    }

    protected PlayAllItemsFoundAnimation() {
        const mask = new PIXI.Graphics();
        mask.beginFill(0xff00000);
        mask.drawRect(
            (MAX_ITEMS - this.icons.length) * MAX_ICON_SIZE,
            -this.height,
            this.width + X_OFFSET,
            this.height,
        );
        mask.endFill();
        mask.position.copy(this.position);
        this.parent.addChild(mask);
        this.mask = mask;
        TweenLite.to(this, 2.5, {pixi: {positionX: -this.width, ease: Back.easeOut}}).delay(5);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case ITEM_FOUND:
                this.PlayItemFoundAnimation();
                break;
            case ALL_ITEMS_FOUND:
                if (!this.allItemsFound) {
                    this.PlayAllItemsFoundAnimation();
                }
                this.allItemsFound = true;
                break;
        }
    }
}
